/**
 * Fix & enhance (WS F, plan §10): turns a source's analysis and correction profile into plain-language issues,
 * and estimates what a paid provider would cost for a source. Pure functions; the panel renders their output.
 * Author: Eli Shvarzman
 */
import {
  AUDIO_CORRECTION_KEYS, AUDIO_ENHANCERS, VIDEO_CORRECTION_KEYS, VIDEO_SCALERS, correctionOn, effectiveCorrections, providerTier,
  type AudioEnhancerProvider, type CorrectionKey, type CorrectionProvider, type Corrections, type VideoScaler,
} from '@castfeeds/schema';
import type { TFunction } from '@castfeeds/i18n';
import type { SessionSource, SourceKind } from '@/lib/api/types';

/** Detection thresholds, the same as the worker's `recommend()` rules (contract §5). */
export const THRESHOLDS = {
  noise: 0.25,
  darkLuma: 0.3,
  cast: 0.06,
  shake: 0.3,
  audioNoiseFloorDb: -55,
  clippingPct: 0.5,
  /** A camera-match LUT is made from this ΔE on. */
  deltaE: 2,
  /** Below this, the darker/brighter sentence is not worth showing; the colour sentence is used instead. */
  lumaPct: 3,
  /** Mic loudness further than this from the other mics reads as "quieter/louder". */
  lufsSpread: 3,
  /** Video pixels blown or crushed above this share are worth a warning. */
  blownPct: 5,
} as const;

/** Product names are never translated. */
export const PROVIDER_NAMES: Record<CorrectionProvider, string> = {
  ffmpeg: 'FFmpeg',
  deepfilternet: 'DeepFilterNet',
  lanczos: 'Lanczos',
  realesrgan: 'Real-ESRGAN',
  auphonic: 'Auphonic',
  elevenlabs: 'ElevenLabs Voice Isolator',
  topaz: 'Topaz',
};

/** Pilot release in which a provider tier arrives (Tier A ships now). */
export const PROVIDER_PILOT: Partial<Record<CorrectionProvider, string>> = { realesrgan: '1.2', auphonic: '1.3', elevenlabs: '1.3', topaz: '1.3' };

/** USD per minute of source, Oct 2026 (contract §6). Topaz at 1080p. */
export const PROVIDER_USD_PER_MIN: Partial<Record<CorrectionProvider, number>> = { auphonic: 0.024, elevenlabs: 0.12, realesrgan: 0.03, topaz: 0.8 };

export interface CostEstimate {
  tier: 'A' | 'B' | 'C';
  usdPerMin: number;
  /** null when the source's duration is unknown. */
  minutes: number | null;
  /** 0 for free providers; at least $0.01 for any paid run; null when the duration is unknown. */
  usd: number | null;
}

/** What running `provider` over a source of `duration_ms` would cost. */
export function costEstimate(provider: CorrectionProvider, duration_ms: number | null | undefined): CostEstimate {
  const tier = providerTier(provider);
  const usdPerMin = PROVIDER_USD_PER_MIN[provider] ?? 0;
  if (duration_ms === null || duration_ms === undefined || !Number.isFinite(duration_ms) || duration_ms < 0) return { tier, usdPerMin, minutes: null, usd: usdPerMin === 0 ? 0 : null };
  const minutes = duration_ms / 60_000;
  const raw = usdPerMin * minutes;
  const usd = raw === 0 ? 0 : Math.max(0.01, Math.round(raw * 100) / 100);
  return { tier, usdPerMin, minutes, usd };
}

export interface ProviderOption<P extends CorrectionProvider> {
  value: P;
  name: string;
  tier: 'A' | 'B' | 'C';
  /** Only Tier A runs in Pilot 1.1. */
  available: boolean;
  pilot: string | null;
  cost: CostEstimate;
}

function options<P extends CorrectionProvider>(list: readonly P[], duration_ms: number | null | undefined): ProviderOption<P>[] {
  return list.map((p) => ({ value: p, name: PROVIDER_NAMES[p], tier: providerTier(p), available: providerTier(p) === 'A', pilot: PROVIDER_PILOT[p] ?? null, cost: costEstimate(p, duration_ms) }));
}
export const voiceOptions = (duration_ms: number | null | undefined) => options<AudioEnhancerProvider>(AUDIO_ENHANCERS, duration_ms);
export const upscalerOptions = (duration_ms: number | null | undefined) => options<VideoScaler>(VIDEO_SCALERS, duration_ms);

export interface SourceIssue {
  /** Stable id within a source (for React keys and tests). */
  id: string;
  /** The correction that repairs it; null for information only (e.g. clipping). */
  key: CorrectionKey | null;
  text: string;
  status: string;
  /** The correction is on in the effective profile (false for info-only issues). */
  on: boolean;
}

const VIDEO_KINDS: readonly SourceKind[] = ['camera', 'program', 'screen', 'upload'];
const MIC_KINDS: readonly SourceKind[] = ['mic', 'mix'];

export const sourceName = (s: Pick<SessionSource, 'label' | 'person' | 'filename'>) => s.label || s.person || s.filename;
export const hasVideo = (s: SessionSource) => (s.analysis ? !!s.analysis.video : VIDEO_KINDS.includes(s.kind));
export const hasAudio = (s: SessionSource) => (s.analysis ? !!s.analysis.audio : true);
export const isVoiceSource = (s: SessionSource) => MIC_KINDS.includes(s.kind);

/** The effective profile: the API's `corrections`, else computed from the recommendation and the overrides. */
export function effectiveOf(s: SessionSource): Corrections {
  if (s.corrections) return s.corrections;
  return effectiveCorrections(s.recommended_corrections ?? null, s.overrides ?? null).corrections;
}

/** The person changed any key (overrides present). */
export const isEdited = (s: SessionSource) => s.corrections_origin === 'human' || (!!s.overrides && Object.keys(s.overrides).length > 0);

const fmtPct = (v: number) => (v >= 10 ? String(Math.round(v)) : String(Math.round(v * 10) / 10));
const median = (xs: number[]) => {
  const v = [...xs].sort((a, b) => a - b);
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
};

/**
 * Plain-language issues for one source, each with its correction and status ("Matched", "Removed", "Off").
 * `reference` is the camera the colour match was made against (its label replaces "the wide shot");
 * `peers` are the session's other sources, used to say a mic is quieter or louder than the rest.
 */
export function describeSource(source: SessionSource, reference: SessionSource | null | undefined, t: TFunction, peers: readonly SessionSource[] = []): SourceIssue[] {
  const a = source.analysis ?? null;
  const eff = effectiveOf(source);
  const rec = source.recommended_corrections ?? null;
  const overrides = source.overrides ?? null;
  const name = sourceName(source);
  const relevant = (k: CorrectionKey) => correctionOn(rec, k) || (!!overrides && Object.prototype.hasOwnProperty.call(overrides, k)) || correctionOn(eff, k);
  const out: SourceIssue[] = [];
  const seen = new Set<CorrectionKey>();
  const fix = (id: string, key: CorrectionKey, text: string, statusOn: string) => {
    const on = correctionOn(eff, key);
    seen.add(key);
    out.push({ id, key, text, status: on ? statusOn : t('fxOff'), on });
  };
  const info = (id: string, text: string) => out.push({ id, key: null, text, status: t('fxCantRepair'), on: false });

  // ── Picture ──
  if (hasVideo(source)) {
    const cm = source.colour_match ?? null;
    const isReference = !!cm && cm.reference_id === source.id;
    if (!isReference && ((cm && cm.delta_e_before >= THRESHOLDS.deltaE) || relevant('colour_lut'))) {
      const ref = reference ? sourceName(reference) : t('fxRefCamera');
      const pct = cm ? Math.abs(cm.luma_diff_pct) : 0;
      if (cm && pct >= THRESHOLDS.lumaPct) {
        fix('colour', 'colour_lut', t(cm.luma_diff_pct < 0 ? 'fxDarker' : 'fxBrighter', { name, pct: Math.round(pct), ref }), t('fxMatched'));
      } else {
        fix('colour', 'colour_lut', t('fxColourDiff', { name, ref }), t('fxMatchedColours'));
      }
    }
    if (a?.interlaced === true || relevant('deinterlace')) fix('interlaced', 'deinterlace', a?.interlaced ? t('fxInterlaced') : t('fxk_deinterlace'), t('fxDeinterlaced'));
    if ((a?.noise_level ?? 0) >= THRESHOLDS.noise || relevant('denoise_video')) fix('grain', 'denoise_video', (a?.noise_level ?? 0) >= THRESHOLDS.noise ? t('fxGrainy') : t('fxk_denoise_video'), t('fxGrainCleaned'));
    const dark = a?.mean_luma !== null && a?.mean_luma !== undefined && a.mean_luma < THRESHOLDS.darkLuma;
    // A matched camera is brightened by its LUT, so "dark" is only an issue when the camera is not matched.
    if ((dark && !relevant('colour_lut')) || relevant('exposure')) fix('dark', 'exposure', dark ? t('fxDark') : t('fxk_exposure'), t('fxBrightened'));
    const cast = (a?.colour?.cast ?? 0) >= THRESHOLDS.cast;
    if ((cast && !relevant('colour_lut')) || relevant('white_balance')) fix('cast', 'white_balance', cast ? t('fxCast') : t('fxk_white_balance'), t('fxBalanced'));
    if ((a?.shake_score ?? 0) >= THRESHOLDS.shake || relevant('stabilize')) fix('shake', 'stabilize', (a?.shake_score ?? 0) >= THRESHOLDS.shake ? t('fxShake') : t('fxk_stabilize'), t('fxStabilised'));
    if (a?.video?.vfr || relevant('fps')) {
      const target = (eff.fps || null)?.fps ?? (rec?.fps || null)?.fps ?? 30;
      fix('vfr', 'fps', a?.video?.vfr ? t('fxVfr') : t('fxk_fps'), t('fxConformed', { fps: target }));
    }
  }

  // ── Sound ──
  const au = a?.audio ?? null;
  if (au?.hum_hz || relevant('hum_notch_hz')) {
    const hz = au?.hum_hz ?? (typeof eff.hum_notch_hz === 'number' ? eff.hum_notch_hz : typeof rec?.hum_notch_hz === 'number' ? rec.hum_notch_hz : 50);
    fix('hum', 'hum_notch_hz', t('fxHum', { name, hz }), t('fxHumRemoved'));
  }
  if ((au && au.noise_floor_db > THRESHOLDS.audioNoiseFloorDb) || relevant('denoise_audio')) {
    const provider = (eff.denoise_audio || null)?.provider ?? (rec?.denoise_audio || null)?.provider ?? 'ffmpeg';
    fix('noise', 'denoise_audio', au && au.noise_floor_db > THRESHOLDS.audioNoiseFloorDb ? t('fxNoise') : t('fxk_denoise_audio'), t('fxNoiseCleaned', { provider: PROVIDER_NAMES[provider] }));
  }
  if (relevant('highpass_hz')) {
    const hz = typeof eff.highpass_hz === 'number' ? eff.highpass_hz : typeof rec?.highpass_hz === 'number' ? rec.highpass_hz : 80;
    fix('rumble', 'highpass_hz', t('fxRumble', { hz }), t('fxFiltered'));
  }
  if (relevant('declick')) fix('clicks', 'declick', t('fxClicks'), t('fxClicksRemoved'));
  if (relevant('loudness') || (au?.lufs !== null && au?.lufs !== undefined && isVoiceSource(source))) {
    const lufs = au?.lufs ?? (eff.loudness || null)?.measured_lufs ?? (rec?.loudness || null)?.measured_lufs ?? null;
    const others = peers.filter((p) => p.id !== source.id && isVoiceSource(p)).map((p) => p.analysis?.audio?.lufs).filter((v): v is number => typeof v === 'number');
    const diff = lufs !== null && others.length ? lufs - median(others) : 0;
    const text = diff <= -THRESHOLDS.lufsSpread ? t('fxQuieter') : diff >= THRESHOLDS.lufsSpread ? t('fxLouder') : lufs !== null ? t('fxLevel', { lufs: Math.round(lufs * 10) / 10 }) : t('fxk_loudness');
    fix('level', 'loudness', text, t('fxLevelled'));
  }

  // Any other correction that is on or was changed (e.g. switched on from "More fixes").
  for (const key of [...VIDEO_CORRECTION_KEYS, ...AUDIO_CORRECTION_KEYS]) {
    if (key === 'upscale' || seen.has(key) || !relevant(key)) continue;
    fix(key, key, t(`fxk_${key}` as const), t('fxOn'));
  }

  // ── Information only (nothing in Tier A repairs these) ──
  if (a?.clipped_pct !== null && a?.clipped_pct !== undefined && a.clipped_pct >= THRESHOLDS.blownPct && hasVideo(source)) info('blown', t('fxBlown', { pct: fmtPct(a.clipped_pct) }));
  if (au && au.clipping_pct >= THRESHOLDS.clippingPct) info('clipping', t('fxClipping', { name, pct: fmtPct(au.clipping_pct) }));
  return out;
}

/** Corrections a person may switch on for this source that are not listed as issues yet. */
export function moreFixes(source: SessionSource, issues: readonly SourceIssue[]): CorrectionKey[] {
  const shown = new Set(issues.map((i) => i.key).filter((k): k is CorrectionKey => !!k));
  const keys: CorrectionKey[] = [];
  // Screen recordings get no picture corrections (contract §5); the camera match needs a LUT from sync.
  if (hasVideo(source) && source.kind !== 'screen') keys.push(...VIDEO_CORRECTION_KEYS.filter((k) => k !== 'upscale' && (k !== 'colour_lut' || !!source.colour_match)));
  if (hasAudio(source)) keys.push(...AUDIO_CORRECTION_KEYS);
  return keys.filter((k) => !shown.has(k));
}

/** The reference camera of a session: the source named by any colour match, else the first camera with video. */
export function referenceOf(sources: readonly SessionSource[], source?: SessionSource): SessionSource | null {
  const id = source?.colour_match?.reference_id ?? sources.find((s) => s.colour_match)?.colour_match?.reference_id;
  return sources.find((s) => s.id === id) ?? sources.find((s) => s.kind === 'camera' && hasVideo(s)) ?? null;
}
